import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { supabase } from "../lib/supabase";
import {
  canAssignNewJobs,
  getEffectiveWorkerStatus,
  isWorkerVisibleWindow,
} from "../utils/workerAvailability";
import {
  canAccessCustomerNavigation,
  getSettingsRoute,
} from "../utils/routeHelpers";

function Header() {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { profile, isLoggedIn, signOut, loading, user } = useAuth();
  const { t } = useLanguage();

  const [workerStatus, setWorkerStatus] = useState("offline");
  const [workerId, setWorkerId] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (!user?.id || profile?.role !== "worker") {
      setWorkerStatus("offline");
      setWorkerId(null);
      return;
    }

    fetchWorkerHeaderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile?.role]);

  const fetchWorkerHeaderData = async () => {
    try {
      const { data: workerData, error: workerError } = await supabase
        .from("workers")
        .select("*")
        .eq("profile_id", user.id)
        .maybeSingle();

      if (workerError) throw workerError;
      if (!workerData) return;

      setWorkerId(workerData.id);

      const { data: jobsData, error: jobsError } = await supabase
        .from("worker_jobs")
        .select("*")
        .eq("worker_id", workerData.id)
        .order("created_at", { ascending: false });

      if (jobsError) throw jobsError;

      const effectiveStatus = getEffectiveWorkerStatus(
        workerData.online_status,
        jobsData || []
      );

      setWorkerStatus(effectiveStatus);

      if (effectiveStatus !== workerData.online_status) {
        await supabase
          .from("workers")
          .update({ online_status: effectiveStatus })
          .eq("id", workerData.id);
      }
    } catch (error) {
      console.error("Worker header fetch error:", error);
    }
  };

  const toggleWorkerStatus = async () => {
    try {
      if (!workerId) return;

      if (!isWorkerVisibleWindow()) {
        alert("Worker can stay visible only between 5:30 AM and 6:30 PM.");
        return;
      }

      if (!canAssignNewJobs() && workerStatus === "offline") {
        alert("No new jobs can be made active after 6:00 PM.");
        return;
      }

      const nextStatus = workerStatus === "online" ? "offline" : "online";

      const { error } = await supabase
        .from("workers")
        .update({ online_status: nextStatus })
        .eq("id", workerId);

      if (error) throw error;

      setWorkerStatus(nextStatus);
    } catch (error) {
      console.error("Toggle worker status error:", error);
      alert(error.message || "Failed to update worker status.");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      alert("Failed to log out.");
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    navigate(q ? `/all-services?q=${encodeURIComponent(q)}` : "/all-services");
  };

  const handleDashboardNavigation = () => {
    navigate(isLoggedIn ? "/dashboard" : "/login");
  };

  const handleSettingsNavigation = () => {
    navigate(getSettingsRoute(profile));
  };

  return (
    <header style={headerWrap}>
      <div style={headerInner} className="app-header__inner">
        <div style={topRow} className="app-header__top-row">
          <Link to="/" style={brand} className="app-header__brand">
            🏗️ {t("header.brand")}
          </Link>

          <div style={rightWrap} className="app-header__actions">
            <button
              onClick={() => navigate("/cart")}
              style={cartBtn}
              className="app-header__button app-header__button--cart"
            >
              🛒 {t("common.cart")} ({cartItems.length})
            </button>

            {loading ? null : !isLoggedIn ? (
              <>
                <Link
                  to="/login"
                  style={outlineBtn}
                  className="app-header__button app-header__link-button"
                >
                  {t("common.login")}
                </Link>
                <Link
                  to="/signup"
                  style={solidBtn}
                  className="app-header__button app-header__link-button"
                >
                  {t("common.signup")}
                </Link>
              </>
            ) : (
              <>
                {profile?.role === "worker" ? (
                  <button
                    onClick={toggleWorkerStatus}
                    style={{
                      ...statusBtn,
                      background:
                        workerStatus === "online" ? "#0f2416" : "#2a1111",
                      color:
                        workerStatus === "online" ? "#7CFC00" : "#ff9a9a",
                      border:
                        workerStatus === "online"
                          ? "1px solid #1f5d34"
                          : "1px solid #6b1d1d",
                    }}
                    className="app-header__button"
                  >
                    {workerStatus === "online" ? "🟢 Online" : "🔴 Offline"}
                  </button>
                ) : null}

                <div style={profilePill} className="app-header__profile-pill">
                  <div style={{ fontWeight: "bold" }}>
                    {profile?.full_name || "User"}
                  </div>
                  <div style={{ color: "#9f9f9f", fontSize: "12px" }}>
                    {profile?.display_user_id || profile?.role}
                  </div>
                </div>

                <button
                  onClick={handleDashboardNavigation}
                  style={outlineBtn}
                  className="app-header__button"
                >
                  {t("common.dashboard")}
                </button>

                <button
                  onClick={handleSettingsNavigation}
                  style={outlineBtn}
                  className="app-header__button"
                >
                  {t("common.settings")}
                </button>

                <button
                  onClick={handleLogout}
                  style={solidBtn}
                  className="app-header__button"
                >
                  {t("common.logout")}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={bottomRow} className="app-header__bottom-row">
          <form
            onSubmit={handleSearchSubmit}
            style={searchForm}
            className="app-header__search-form"
          >
            <span style={searchIcon}>🔎</span>
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search services, appliances, maid, tutor..."
              style={searchInput}
              className="app-header__search-input"
            />
          </form>

          <div style={navLinks} className="app-header__nav-links">
            <button
              onClick={() => navigate("/all-services")}
              style={navButton}
              className="app-header__button app-header__nav-button"
            >
              All Services
            </button>
            {canAccessCustomerNavigation(profile?.role) ? (
              <>
                <button
                  onClick={() => navigate("/materials")}
                  style={navButton}
                  className="app-header__button app-header__nav-button"
                >
                  Materials
                </button>
                <button
                  onClick={() => navigate("/customer-rate-card")}
                  style={navButton}
                  className="app-header__button app-header__nav-button"
                >
                  Rate Cards
                </button>
                <button
                  onClick={() => navigate("/my-bookings")}
                  style={navButton}
                  className="app-header__button app-header__nav-button"
                >
                  My Bookings
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}

const headerWrap = {
  position: "sticky",
  top: 0,
  zIndex: 1100,
  background: "rgba(5,5,5,0.92)",
  borderBottom: "1px solid #1f1f1f",
  backdropFilter: "blur(10px)",
};

const headerInner = {
  maxWidth: "1280px",
  margin: "0 auto",
  padding: "14px 20px",
  display: "grid",
  gap: "14px",
};

const topRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

const bottomRow = {
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr",
  gap: "14px",
  alignItems: "center",
};

const brand = {
  color: "#fff",
  textDecoration: "none",
  fontWeight: "bold",
  fontSize: "20px",
};

const rightWrap = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
};

const searchForm = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  background: "#111",
  border: "1px solid #262626",
  borderRadius: "14px",
  padding: "10px 14px",
};

const searchIcon = {
  fontSize: "16px",
  lineHeight: 1,
};

const searchInput = {
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  color: "#fff",
  fontSize: "15px",
};

const navLinks = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const navButton = {
  padding: "10px 14px",
  border: "1px solid #2a2a2a",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const cartBtn = {
  padding: "10px 14px",
  border: "1px solid #2a2a2a",
  background: "#171717",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const profilePill = {
  color: "#fff",
  fontSize: "14px",
  background: "#111",
  border: "1px solid #222",
  borderRadius: "12px",
  padding: "10px 12px",
  minWidth: "180px",
};

const outlineBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  border: "1px solid #fff",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const solidBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const statusBtn = {
  padding: "10px 14px",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

export default Header;
